import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import BackendAxios from "../BackendAxios";
import {
  PDF_PAGE_IMAGE_OPTIONS,
  renderPdfPagesToImageFiles,
} from "../Utils/pdfPageImages";

function getAssetDisplayName(asset) {
  if (asset?.fileName) {
    return asset.fileName;
  }

  if (!asset?.url) {
    return "Asset";
  }

  try {
    return decodeURIComponent(asset.url.split("/").pop() || "Asset");
  } catch (error) {
    return asset.url.split("/").pop() || "Asset";
  }
}

function moveItem(array, fromIndex, toIndex) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
    return array;
  }

  const nextArray = [...array];
  const [movedItem] = nextArray.splice(fromIndex, 1);
  nextArray.splice(toIndex, 0, movedItem);
  return nextArray;
}

function getAssetKindFromFile(file) {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name) ? "pdf" : "image";
}

function getAssetPreviewSource(item) {
  if (item.source === "existing") {
    if (item.kind === "pdf") {
      return item.asset?.previewUrl || "";
    }
    return item.asset?.url || "";
  }

  if (item.kind === "pdf") {
    return item.previewDataUrl || "";
  }

  return item.objectUrl || "";
}

function getGalleryAssetPreviewSource(asset) {
  if (!asset) return "";
  if (asset.kind === "pdf") {
    return asset.previewUrl || "";
  }
  return asset.url || asset.previewUrl || "";
}

function GalleryCategoryPreviewStrip({ assets = [] }) {
  const containerRef = React.useRef(null);
  const [previewLayout, setPreviewLayout] = React.useState({
    visibleCount: 1,
    thumbSize: 36,
    gap: 4,
  });
  const previewableAssets = React.useMemo(
    () => assets.filter((asset) => getGalleryAssetPreviewSource(asset)),
    [assets]
  );

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const updateVisibleCount = () => {
      const width = container.getBoundingClientRect().width;
      const viewportWidth =
        typeof window === "undefined" ? 1200 : window.innerWidth;
      const screenLayout =
        viewportWidth < 600
          ? { maxCount: 1, thumbSize: 34, gap: 4, badgeWidth: 32 }
          : viewportWidth < 900
          ? { maxCount: 3, thumbSize: 38, gap: 4, badgeWidth: 34 }
          : viewportWidth < 1200
          ? { maxCount: 6, thumbSize: 40, gap: 5, badgeWidth: 38 }
          : { maxCount: 10, thumbSize: 44, gap: 6, badgeWidth: 42 };
      const overflowBadgeWidth =
        previewableAssets.length > 1 ? screenLayout.badgeWidth : 0;
      const availableWidth = Math.max(
        screenLayout.thumbSize,
        width - overflowBadgeWidth
      );
      const nextCount = Math.max(
        1,
        Math.min(
          screenLayout.maxCount,
          previewableAssets.length,
          Math.floor(
            (availableWidth + screenLayout.gap) /
              (screenLayout.thumbSize + screenLayout.gap)
          )
        )
      );
      setPreviewLayout({
        visibleCount: nextCount || 1,
        thumbSize: screenLayout.thumbSize,
        gap: screenLayout.gap,
      });
    };

    updateVisibleCount();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateVisibleCount);
      return () => window.removeEventListener("resize", updateVisibleCount);
    }

    const observer = new ResizeObserver(updateVisibleCount);
    observer.observe(container);
    return () => observer.disconnect();
  }, [previewableAssets.length]);

  if (previewableAssets.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No previews
      </Typography>
    );
  }

  const shownAssets = previewableAssets.slice(0, previewLayout.visibleCount);
  const hiddenCount = Math.max(0, previewableAssets.length - shownAssets.length);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: `${previewLayout.gap}px`,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {shownAssets.map((asset, index) => (
        <Box
          key={asset.storagePath || asset.url || `gallery-preview-${index}`}
          component="img"
          src={getGalleryAssetPreviewSource(asset)}
          alt={asset.fileName || `Gallery preview ${index + 1}`}
          sx={{
            width: previewLayout.thumbSize,
            height: previewLayout.thumbSize,
            objectFit: "cover",
            border: "1px solid #d5d9df",
            backgroundColor: "#f5f7fa",
            flex: "0 0 auto",
          }}
        />
      ))}
      {hiddenCount > 0 && (
        <Box
          sx={{
            height: previewLayout.thumbSize,
            minWidth: Math.max(30, previewLayout.thumbSize - 8),
            px: 0.75,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #d5d9df",
            backgroundColor: "#f5f7fa",
            color: "#5f6f86",
            fontSize: 12,
            fontWeight: 700,
            flex: "0 0 auto",
          }}
        >
          +{hiddenCount}
        </Box>
      )}
    </Box>
  );
}

const IMAGE_RATIO_TARGETS = [
  { label: "horizontal", width: 1920, height: 1080, ratio: 1920 / 1080 },
  { label: "square", width: 1920, height: 1920, ratio: 1 },
  { label: "vertical", width: 1080, height: 1920, ratio: 1080 / 1920 },
];

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Failed to read image file: ${file.name}`));
    };

    image.src = objectUrl;
  });
}

async function canvasToFile(canvas, fileName, mimeType) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error(`Failed to process image: ${fileName}`));
          return;
        }

        const normalizedName = fileName.replace(/\.[^.]+$/, "");
        const extension = mimeType === "image/png" ? "png" : "jpg";
        resolve(
          new File([blob], `${normalizedName}.${extension}`, {
            type: mimeType,
            lastModified: Date.now(),
          })
        );
      },
      mimeType,
      mimeType === "image/png" ? undefined : 0.9
    );
  });
}

async function normalizeImageFile(file) {
  const image = await loadImageElement(file);
  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const nearestTarget = IMAGE_RATIO_TARGETS.reduce((closest, target) => {
    const targetDelta = Math.abs(target.ratio - sourceRatio);
    const closestDelta = Math.abs(closest.ratio - sourceRatio);
    return targetDelta < closestDelta ? target : closest;
  }, IMAGE_RATIO_TARGETS[0]);

  const canvas = document.createElement("canvas");
  canvas.width = nearestTarget.width;
  canvas.height = nearestTarget.height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error(`Failed to process image: ${file.name}`);
  }

  const targetRatio = nearestTarget.width / nearestTarget.height;
  let cropWidth = image.naturalWidth;
  let cropHeight = image.naturalHeight;
  let cropX = 0;
  let cropY = 0;

  if (sourceRatio > targetRatio) {
    cropWidth = image.naturalHeight * targetRatio;
    cropX = (image.naturalWidth - cropWidth) / 2;
  } else if (sourceRatio < targetRatio) {
    cropHeight = image.naturalWidth / targetRatio;
    cropY = (image.naturalHeight - cropHeight) / 2;
  }

  context.drawImage(
    image,
    cropX,
    cropY,
    cropWidth,
    cropHeight,
    0,
    0,
    nearestTarget.width,
    nearestTarget.height
  );

  const outputMimeType =
    file.type === "image/png" || file.type === "image/webp" ? "image/png" : "image/jpeg";

  return canvasToFile(canvas, file.name, outputMimeType);
}

class GalleryManagement extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      categories: [],
      loading: false,
      error: null,
      successMessage: null,
      dialogOpen: false,
      editingCategory: null,
      categoryName: "",
      assetItems: [],
      draggedAssetId: null,
      draggedCategoryId: null,
      deleteTargetCategory: null,
      processingPdfUploads: false,
    };
  }

  componentDidMount() {
    this.fetchCategories();
  }

  componentWillUnmount() {
    this.state.assetItems.forEach((item) => {
      if (item?.objectUrl) {
        URL.revokeObjectURL(item.objectUrl);
      }
    });
  }

  fetchCategories = async () => {
    this.setState({ loading: true, error: null });

    try {
      const response = await BackendAxios.get("/gallery/admin");
      this.setState({
        categories: response.data.categories || [],
        loading: false,
      });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to fetch gallery categories",
        loading: false,
      });
    }
  };

  handleOpenDialog = (category = null) => {
    const existingAssetItems = (category?.assets || []).map((asset, index) => ({
      id: asset.storagePath || asset._id || `existing-${index}`,
      source: "existing",
      kind: asset.kind,
      name: getAssetDisplayName(asset),
      asset,
    }));

    this.setState({
      dialogOpen: true,
      editingCategory: category,
      categoryName: category?.name || "",
      assetItems: existingAssetItems,
      draggedAssetId: null,
      error: null,
      successMessage: null,
      processingPdfUploads: false,
    });
  };

  handleCloseDialog = () => {
    this.state.assetItems.forEach((item) => {
      if (item?.objectUrl) {
        URL.revokeObjectURL(item.objectUrl);
      }
    });

    this.setState({
      dialogOpen: false,
      editingCategory: null,
      categoryName: "",
      assetItems: [],
      draggedAssetId: null,
      error: null,
      successMessage: null,
      processingPdfUploads: false,
    });
  };

  handleAssetAdd = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      return;
    }

    this.setState({ loading: true, error: null, processingPdfUploads: true });

    try {
      const newItems = [];

      for (const file of files) {
        const kind = getAssetKindFromFile(file);
        const uploadFiles =
          kind === "pdf"
            ? await renderPdfPagesToImageFiles(file, PDF_PAGE_IMAGE_OPTIONS)
            : [await normalizeImageFile(file)];

        uploadFiles.forEach((processedFile, index) => {
          newItems.push({
            id: `new-${Date.now()}-${newItems.length}-${index}-${processedFile.name}`,
            source: "new",
            kind: "image",
            name: processedFile.name,
            file: processedFile,
            objectUrl: URL.createObjectURL(processedFile),
            previewDataUrl: "",
          });
        });
      }

      this.setState((prevState) => ({
        assetItems: [...prevState.assetItems, ...newItems],
        loading: false,
      }));
    } catch (error) {
      this.setState({
        error: error.message || "Failed to process one or more images",
        loading: false,
        processingPdfUploads: false,
      });
    } finally {
      this.setState({ processingPdfUploads: false });
    }

    event.target.value = "";
  };

  handleAssetRemove = (assetId) => {
    this.setState((prevState) => {
      const assetToRemove = prevState.assetItems.find((item) => item.id === assetId);
      if (assetToRemove?.objectUrl) {
        URL.revokeObjectURL(assetToRemove.objectUrl);
      }

      return {
        assetItems: prevState.assetItems.filter((item) => item.id !== assetId),
      };
    });
  };

  handleDragStart = (assetId) => {
    this.setState({ draggedAssetId: assetId });
  };

  handleDragEnd = () => {
    this.setState({ draggedAssetId: null });
  };

  handleDrop = (targetAssetId) => {
    this.setState((prevState) => {
      const fromIndex = prevState.assetItems.findIndex(
        (item) => item.id === prevState.draggedAssetId
      );
      const toIndex = prevState.assetItems.findIndex((item) => item.id === targetAssetId);

      return {
        assetItems: moveItem(prevState.assetItems, fromIndex, toIndex),
        draggedAssetId: null,
      };
    });
  };

  handleCategoryDragStart = (categoryId) => {
    this.setState({ draggedCategoryId: categoryId });
  };

  handleCategoryDragEnd = () => {
    this.setState({ draggedCategoryId: null });
  };

  handleCategoryDrop = (targetCategoryId) => {
    this.setState((prevState) => {
      const fromIndex = prevState.categories.findIndex(
        (category) => category._id === prevState.draggedCategoryId
      );
      const toIndex = prevState.categories.findIndex(
        (category) => category._id === targetCategoryId
      );

      return {
        categories: moveItem(prevState.categories, fromIndex, toIndex),
        draggedCategoryId: null,
      };
    });
  };

  handleSaveCategoryOrder = async () => {
    this.setState({ loading: true, error: null, successMessage: null });

    try {
      const categoryOrder = this.state.categories.map((category, index) => ({
        id: category._id,
        displayOrder: index,
      }));

      const response = await BackendAxios.put("/gallery/admin/order", {
        categoryOrder,
      });

      this.setState({
        categories: response.data.categories || this.state.categories,
        loading: false,
        successMessage: "Category order updated successfully",
      });
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to update category order",
        loading: false,
      });
    }
  };

  handleSave = async () => {
    const {
      editingCategory,
      categoryName,
      assetItems,
    } = this.state;

    if (!categoryName.trim()) {
      this.setState({ error: "Category name is required" });
      return;
    }

    this.setState({ loading: true, error: null, successMessage: null });

    try {
      const existingAssets = assetItems
        .filter((item) => item.source === "existing")
        .map((item) => item.asset);
      const newAssets = assetItems.filter((item) => item.source === "new");
      const formData = new FormData();
      formData.append("name", categoryName.trim());
      formData.append("existingAssets", JSON.stringify(existingAssets));
      formData.append("assetOrder", JSON.stringify(
        assetItems.map((item) => ({
          source: item.source,
          key: item.source === "existing" ? item.asset.storagePath : item.id,
        }))
      ));
      formData.append(
        "newAssetKeys",
        JSON.stringify(newAssets.map((item) => item.id))
      );
      formData.append(
        "previews",
        JSON.stringify(newAssets.map((item) => item.previewDataUrl || ""))
      );

      newAssets.forEach((item) => {
        formData.append("assets", item.file);
      });

      if (editingCategory) {
        await BackendAxios.put(`/gallery/admin/${editingCategory._id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.setState({ successMessage: "Gallery category updated successfully" });
      } else {
        await BackendAxios.post("/gallery/admin", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        this.setState({ successMessage: "Gallery category created successfully" });
      }

      setTimeout(() => {
        this.fetchCategories();
        this.handleCloseDialog();
      }, 900);
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to save gallery category",
        loading: false,
      });
    }
  };

  handleDelete = async () => {
    const categoryId = this.state.deleteTargetCategory?._id;
    if (!categoryId) {
      return;
    }

    this.setState({ loading: true, error: null, successMessage: null });

    try {
      await BackendAxios.delete(`/gallery/admin/${categoryId}`);
      this.setState({
        successMessage: "Gallery category deleted successfully",
        deleteTargetCategory: null,
      });
      this.fetchCategories();
    } catch (error) {
      this.setState({
        error: error.response?.data?.error || "Failed to delete gallery category",
        loading: false,
        deleteTargetCategory: null,
      });
    }
  };

  render() {
    const {
      categories,
      loading,
      error,
      successMessage,
      dialogOpen,
      editingCategory,
      categoryName,
      assetItems,
      draggedAssetId,
      draggedCategoryId,
      deleteTargetCategory,
      processingPdfUploads,
    } = this.state;

    return (
      <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2 }}>
          <Box>
            <Typography variant="h5">Gallery Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Create project categories and upload images or PDFs for the gallery rewrite.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => this.handleOpenDialog()}
            sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
          >
            Create Category
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => this.setState({ error: null })}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => this.setState({ successMessage: null })}
          >
            {successMessage}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle1">Categories</Typography>
              <Typography variant="body2" color="text.secondary">
                Drag categories to control the order they appear in the gallery.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={this.handleSaveCategoryOrder}
              disabled={loading || categories.length < 2}
            >
              Save Category Order
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 72 }}>Order</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell sx={{ width: "34%", minWidth: { xs: 92, sm: 132, md: 180 } }}>
                    Previews
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>Assets</TableCell>
                  <TableCell sx={{ width: 120 }}>Updated</TableCell>
                  <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No gallery categories found
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category, index) => (
                    <TableRow
                      key={category._id}
                      draggable
                      onDragStart={() => this.handleCategoryDragStart(category._id)}
                      onDragEnd={this.handleCategoryDragEnd}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => this.handleCategoryDrop(category._id)}
                      sx={{
                        cursor: categories.length > 1 ? "grab" : "default",
                        backgroundColor:
                          draggedCategoryId === category._id
                            ? "rgba(91, 113, 149, 0.12)"
                            : "transparent",
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DragIndicatorIcon sx={{ color: "#7b8798", fontSize: 18 }} />
                          <Typography variant="body2" color="text.secondary">
                            {index + 1}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>
                        <GalleryCategoryPreviewStrip assets={category.assets || []} />
                      </TableCell>
                      <TableCell>{category.assets?.length || 0}</TableCell>
                      <TableCell>
                        {category.updatedAt
                          ? new Date(category.updatedAt).toLocaleDateString()
                          : "Not set"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => this.handleOpenDialog(category)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.setState({ deleteTargetCategory: category })}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Dialog open={dialogOpen} onClose={this.handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCategory ? "Edit Gallery Category" : "Create Gallery Category"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
              <TextField
                label="Category Name"
                value={categoryName}
                onChange={(event) => this.setState({ categoryName: event.target.value })}
                fullWidth
                required
              />

              {assetItems.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Asset Order:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Drag items to set the display order inside this category.
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 72 }}>Order</TableCell>
                          <TableCell sx={{ width: 88 }}>Preview</TableCell>
                          <TableCell>File</TableCell>
                          <TableCell sx={{ width: 120 }}>Status</TableCell>
                          <TableCell align="right" sx={{ width: 72 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {assetItems.map((item, index) => (
                          <TableRow
                            key={item.id}
                            draggable
                            onDragStart={() => this.handleDragStart(item.id)}
                            onDragEnd={this.handleDragEnd}
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={() => this.handleDrop(item.id)}
                            sx={{
                              cursor: "grab",
                              backgroundColor:
                                draggedAssetId === item.id
                                  ? "rgba(91, 113, 149, 0.12)"
                                  : "transparent",
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <DragIndicatorIcon sx={{ color: "#7b8798", fontSize: 18 }} />
                                <Typography variant="body2" color="text.secondary">
                                  {index + 1}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  width: 56,
                                  height: 56,
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  border: "1px solid #d5d9df",
                                  backgroundColor: "#f5f7fa",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {getAssetPreviewSource(item) ? (
                                  <Box
                                    component="img"
                                    src={getAssetPreviewSource(item)}
                                    alt={item.name}
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      display: "block",
                                    }}
                                  />
                                ) : item.kind === "pdf" ? (
                                  <DescriptionIcon sx={{ color: "#6a7686" }} />
                                ) : (
                                  <ImageIcon sx={{ color: "#6a7686" }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>
                              {item.source === "existing" ? "Saved asset" : "New upload"}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => this.handleAssetRemove(item.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add Images or PDFs:
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  PDF uploads are converted into one image per page.
                </Typography>
                <input
                  accept="image/*,application/pdf"
                  id="gallery-asset-upload"
                  style={{ display: "none" }}
                  type="file"
                  multiple
                  onChange={this.handleAssetAdd}
                />
                <label htmlFor="gallery-asset-upload">
                  <Button variant="outlined" component="span" startIcon={<AddIcon />}>
                    Upload Files
                  </Button>
                </label>
              </Box>

              {processingPdfUploads && (
                <Alert severity="info" icon={<CircularProgress size={18} />}>
                  Converting PDF pages. Large plan files can take a moment to prepare.
                </Alert>
              )}

            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseDialog}>Cancel</Button>
            <Button
              onClick={this.handleSave}
              variant="contained"
              disabled={loading}
              sx={{ backgroundColor: "#5B7195", "&:hover": { backgroundColor: "#4a5d7a" } }}
            >
              {loading ? <CircularProgress size={24} /> : editingCategory ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteTargetCategory)}
          onClose={() => this.setState({ deleteTargetCategory: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Category</DialogTitle>
          <DialogContent>
            <Typography>
              Delete
              {" "}
              <strong>{deleteTargetCategory?.name || "this category"}</strong>
              {" "}
              and all of its gallery assets?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ deleteTargetCategory: null })}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={this.handleDelete}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }
}

export default GalleryManagement;
